// =====================================================================
// commande-prete/stockage.js — STOCKAGE SUR DALLE (22-10-2026) — réglage du client (Paramètres clients)
// =====================================================================
// Jumeau des règles du serveur (backend/src/services/ready-stockage.rules.js, que le front ne peut pas
// importer) : mêmes bornes, mêmes messages. Fonctions PURES : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

export const MAX_FRANCHISE_JOURS = 365;
export const MAX_PRIX_CENTIMES = 100_000;   // 1 000 € par palette et par jour

export const MSG_STOCKAGE_FRANCHISE = `Stockage sur dalle — jours gratuits : un nombre entier de 0 à ${MAX_FRANCHISE_JOURS}.`;
export const MSG_STOCKAGE_PRIX = 'Stockage sur dalle — prix par palette et par jour : de 0,01 à 1 000 € (ex. 2,50).';
export const MSG_STOCKAGE_DEPUIS = 'Stockage sur dalle — facturé à partir du : choisissez une date.';
export const MSG_STOCKAGE_PROJET = 'Stockage sur dalle : choisissez le projet de facturation (les lignes vont dans ce projet).';
export const MSG_STOCKAGE_PROJET_CLIENT = 'Stockage sur dalle : choisissez un projet client — un projet interne n\'apparaît pas dans Facturation.';
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const libelleMois = (mois) => `${MOIS_FR[Number(mois.slice(5, 7)) - 1]} ${mois.slice(0, 4)}`;
/** « facturé à partir du » touchant un mois clôturé (verrouillé) : il n'y serait jamais calculé. */
export const msgStockageMoisClos = (mois) => `Stockage sur dalle — facturé à partir du : ${libelleMois(mois)} est clôturé (verrouillé dans Facturation) — choisissez une date dans un mois ouvert.`;
const decalerMois = (mois, n) => new Date(Date.UTC(Number(mois.slice(0, 4)), Number(mois.slice(5, 7)) - 1 + n, 1)).toISOString().slice(0, 7);
/** Mois qu'un « facturé à partir du » choisi aujourd'hui toucherait déjà : le sien jusqu'au mois en cours — aucun ne doit être clôturé. */
export function moisAVerifier(choisi, aujourdhui) {
  const liste = [choisi.slice(0, 7)];
  for (let m = decalerMois(liste[0], 1); m <= aujourdhui.slice(0, 7); m = decalerMois(m, 1)) liste.push(m);
  return liste;
}
/** Date proposée quand on coche la case : aujourd'hui, ou le 1er du premier mois ouvert si le mois en cours est clôturé. */
export function premierJourOuvert(aujourdhui, verrouilles) {
  const v = verrouilles || new Set();
  if (!v.has(aujourdhui.slice(0, 7))) return aujourdhui;
  let m = aujourdhui.slice(0, 7);
  for (let i = 0; i < 12 && v.has(m); i++) m = decalerMois(m, 1);
  return `${m}-01`;
}

/** « 2,50 » / « 2.5 » / « 2 » / « 2,50 € » → centimes ; vide → null ; illisible → NaN. */
export function prixEnCentimes(texte) {
  const s = String(texte ?? '').replace(/[\s €]/g, '');
  if (!s) return null;
  if (!/^\d{1,7}([.,]\d{1,2})?$/.test(s)) return NaN;
  const [e, d = ''] = s.replace(',', '.').split('.');
  return Number(e) * 100 + Number(d.padEnd(2, '0'));
}
/** Centimes → « 2,50 » (le champ du formulaire). */
export const centimesEnTexte = (c) => (Number.isInteger(c) && c >= 0 ? `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}` : '');
/** « 5 » → 5 ; vide → null ; illisible → NaN. */
export function joursEnEntier(texte) {
  const s = String(texte ?? '').trim();
  if (!s) return null;
  return /^\d{1,4}$/.test(s) ? Number(s) : NaN;
}

const JOUR_RE = /^\d{4}-\d{2}-\d{2}$/;
export function jourValide(j) {
  if (typeof j !== 'string' || !JOUR_RE.test(j)) return false;
  const d = new Date(`${j}T00:00:00Z`);   // mois 13, jour 32… : date invalide (toISOString lèverait une exception)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === j;
}
export const texteJour = (j) => `${j.slice(8, 10)}/${j.slice(5, 7)}/${j.slice(0, 4)}`;
/** Bornes de « facturé à partir du » choisi le jour `aujourdhui` (« YYYY-MM-DD ») : du 1er du mois précédent à dans un an. */
export function bornesDepuis(aujourdhui) {
  const d = new Date(Date.UTC(Number(aujourdhui.slice(0, 4)), Number(aujourdhui.slice(5, 7)) - 2, 1));
  return { min: d.toISOString().slice(0, 10), max: `${Number(aujourdhui.slice(0, 4)) + 1}${aujourdhui.slice(4)}` };
}

const virgule = (n) => n.toFixed(2).replace('.', ',');
/** Exemple chiffré sous les champs : une palette restée 20 j 18 h (l'exemple de Silviu) ; null si un champ est illisible. */
export function exempleStockage(franchise, centimes) {
  if (!Number.isInteger(franchise) || !Number.isInteger(centimes) || franchise < 0 || centimes < 1) return null;
  const heures = Math.max(0, 20 * 24 + 18 - franchise * 24);
  const montant = Math.round((heures * centimes) / 24);
  return `Exemple : une palette restée 20 j 18 h sur la dalle → ${franchise} j gratuit${franchise > 1 ? 's' : ''}, `
    + `${virgule(heures / 24)} j facturés × ${virgule(centimes / 100)} € = ${virgule(montant / 100)} €.`;
}
